import { ApolloLink, Observable } from '@apollo/client'
import { FieldNode, getOperationAST, Kind } from 'graphql'
import { failureOutcome, isPerformanceMonitoringEnabled, Outcome, startTrace } from 'utils/performance'

const verificationOperations = new Set([
  'performOCR',
  'performOCRandDigitallyExtract',
  'udyamRegnSearch',
  'processPennyDrop',
  'validateIfsc',
  'createBankAccount',
  'updateBankAccount',
  'panDetails',
  'gstDetails',
  'gstinsFromPAN',
])

export const performanceLink = new ApolloLink(
  (operation, forward) => {
    if (!isPerformanceMonitoringEnabled()) return forward(operation)
    return new Observable((observer) => {
      const definition = getOperationAST(operation.query, operation.operationName || undefined)
      const fields =
        definition?.selectionSet.selections
          .filter((item): item is FieldNode => item.kind === Kind.FIELD)
          .map((item) => item.name.value) || []
      const operationName = fields.join('_').slice(0, 80) || 'unknown'
      const finish = startTrace('edge_api', { operation: operationName, transport: 'graphql' })
      const finishVerification = fields.some((field) => verificationOperations.has(field))
        ? startTrace('edge_ocr', { operation: operationName })
        : undefined
      const finishRequest = (
        outcome: Outcome,
        status = operation.getContext().response?.status || 200,
      ) => {
        finish(outcome, { http_status: status })
        finishVerification?.(outcome)
      }
      let hasResponse = false
      let failed = false
      let hasMore = false
      let subscription: { unsubscribe(): void } | undefined
      try {
        subscription = forward(operation).subscribe({
          next: (result) => {
            const streamed = result as {
              hasNext?: boolean
              incremental?: Array<{ errors?: readonly unknown[] }>
            }
            hasResponse = true
            failed =
              failed ||
              Boolean(result.errors?.length) ||
              Boolean(streamed.incremental?.some((part) => part.errors?.length))
            hasMore = streamed.hasNext === true
            observer.next(result)
          },
          error: (error) => {
            finishRequest(failureOutcome(error), error.statusCode || 0)
            observer.error(error)
          },
          complete: () => {
            finishRequest(failed || !hasResponse || hasMore ? 'error' : 'success')
            observer.complete()
          },
        })
      } catch (error) {
        finishRequest(failureOutcome(error), 0)
        observer.error(error)
      }
      return () => {
        finishRequest('cancelled', 0)
        subscription?.unsubscribe()
      }
    })
  },
)
